import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { InterventionStore } from '../../../intervention.store';
import { TreatmentGoalResponse } from '../../../intervention.model';

const MAX_GOALS = 5;

type GoalRow = FormGroup<{ id: FormControl<string | null>; text: FormControl<string> }>;

// The treatment goals of the case (MW-161, Wizard 6): the specific objectives the provider has to address, entered as part of Treatment planning and
// needed before the assessment can be locked, for the types that have goals. The provider that accepted the case reads them and the close-out report
// records what was achieved against each. Only the goals are saved here; the rest of the referral summary is not part of the wizard.
@Component({
  selector: 'treatment-goals-panel',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './treatment-goals-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TreatmentGoalsPanelComponent {
  protected readonly store = inject(InterventionStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly maxGoals = MAX_GOALS;
  // The goals as they are typed (one row each); they start from what is saved. The rows are a signal, so the page follows when they are filled
  // again (a form array changed from an effect is not seen by the page that has already been checked).
  protected readonly goals = signal<GoalRow[]>([]);
  // A goal was added or removed since the rows were filled (a row that is typed in knows for itself that it was).
  private readonly listChanged = signal(false);
  // The saved goals (of the case) that the rows were last filled from: a reload that finds them unchanged leaves the rows, and what is being typed, alone.
  private filledFrom = '';

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  constructor() {
    // The server withholds the goals from a provider until they accept the referral, so they are read again whenever the referrals change.
    effect(() => {
      this.store.referrals();
      this.store.loadReferralSummary(this.interventionId());
    });

    // What is saved is what is shown: the rows are filled again whenever the saved goals change.
    effect(() => this.fill(this.store.referralSummary().treatmentGoals));
  }

  protected add(): void {
    if (this.goals().length < MAX_GOALS) {
      this.goals.update((rows) => [...rows, this.newRow(null, '')]);
      this.listChanged.set(true);
    }
  }

  protected remove(index: number): void {
    this.goals.update((rows) => rows.filter((_, position) => position !== index));
    this.listChanged.set(true);
  }

  protected canSave(): boolean {
    const rows = this.goals();
    const changed = this.listChanged() || rows.some((row) => row.dirty);
    return changed && rows.every((row) => !!row.getRawValue().text.trim()) && !this.store.savingReferralSummary();
  }

  protected save(): void {
    if (!this.canSave()) {
      return;
    }

    this.store.saveReferralSummary(
      this.interventionId(),
      { treatmentGoals: this.goals().map((row) => row.getRawValue()).map((row) => ({ id: row.id, goal: row.text.trim() })) },
      () => this.markSaved(),
    );
  }

  private fill(saved: TreatmentGoalResponse[]): void {
    const snapshot = JSON.stringify([this.interventionId(), saved.map((goal) => [goal.id, goal.goal])]);
    if (snapshot === this.filledFrom) {
      return;
    }

    this.filledFrom = snapshot;
    this.goals.set(saved.map((goal) => this.newRow(goal.id, goal.goal)));
    this.listChanged.set(false);
  }

  // The goals were saved: what is shown is what is saved (the rows are filled again when the saved goals came back different).
  private markSaved(): void {
    this.goals().forEach((row) => row.markAsPristine());
    this.listChanged.set(false);
  }

  private newRow(id: string | null, text: string): GoalRow {
    return this.formBuilder.nonNullable.group({ id: this.formBuilder.control<string | null>(id), text: [text] });
  }
}
